import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import { User } from '../../../core/models/user.model';
import { UserFilter } from '../../../core/models/user-filter.model';
import { PagedResult } from '../../../core/models/paged-result.model';
import { LoggingService } from '../../../core/services/logging.service';
import { BaseRepository } from './base.repository';
import envConfig from '../../../env-config';

interface BackendPagedResult<T> {
  items: T[];
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface ApiResponse<T> {
  data: T;
  statusCode: number;
  message: string;
  isSuccess: boolean;
  errorType: string | null;
}

@Injectable({ providedIn: 'root' })
export class UsersRepository extends BaseRepository {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(LoggingService);
  private readonly apiUrl = envConfig.bffUrl ?? '';

  getUsers(filter: UserFilter): Observable<PagedResult<User>> {
    const params = this.buildFilterParams(filter, true);

    this.logger.debug('UsersRepository: Fetching users from API', { filter });

    return this.http
      .get<ApiResponse<BackendPagedResult<User>>>(
        `${this.apiUrl}/api/v1/user`,
        {
          params,
          context: this.authContext(),
        }
      )
      .pipe(
        map(response => this.transformResponse(response.data)),
        tap(result =>
          this.logger.debug('UsersRepository: Users fetched', {
            count: result.items.length,
          })
        ),
        catchError(error => {
          this.logger.error('UsersRepository: Failed to fetch users', error);
          throw error;
        })
      );
  }

  exportUsers(
    format: 'csv' | 'html',
    filter: UserFilter = {}
  ): Observable<Blob> {
    const params = this.buildFilterParams(filter).set('format', format);

    this.logger.debug('UsersRepository: Exporting users', { format });

    return this.http
      .get<Blob>(`${this.apiUrl}/api/v1/user/export`, {
        params,
        responseType: 'blob' as 'json',
        context: this.authContext(),
      })
      .pipe(
        tap(() => this.logger.debug('UsersRepository: Users exported')),
        catchError(error => {
          this.logger.error('UsersRepository: Failed to export users', error);
          throw error;
        })
      );
  }

  private buildFilterParams(
    filter: UserFilter,
    includePaging = false
  ): HttpParams {
    let params = new HttpParams();

    if (includePaging) {
      params = params
        .set('page', (filter.page ?? 1).toString())
        .set('pageSize', (filter.pageSize ?? 25).toString());
    }

    type QueryParamValue = string | number | boolean | null | undefined;
    const filters: [string, QueryParamValue][] = [
      ['search', filter.search?.trim()],
      ['status', filter.status],
      ['roleName', filter.roleName?.trim()],
      ['sortBy', filter.sortBy],
      ['sortOrder', filter.sortOrder],
      ['idFilter', filter.id?.trim()],
      ['userNameFilter', filter.userName?.trim()],
      ['firstNameFilter', filter.firstName?.trim()],
      ['lastNameFilter', filter.lastName?.trim()],
      ['emailFilter', filter.email?.trim()],
      ['workPhoneFilter', filter.workPhoneNumber?.trim()],
      ['roleNameFilter', filter.roleNameColumn?.trim()],
      ['activeFilter', filter.activeColumn?.trim()],
      ['adGroupFilter', filter.adGroup?.trim()],
    ];

    for (const [key, value] of filters) {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    }

    return params;
  }

  private transformResponse(
    response: BackendPagedResult<User>
  ): PagedResult<User> {
    return {
      items: response.items,
      totalCount: response.totalCount,
      pageNumber: response.page,
      pageSize: response.pageSize,
      totalPages: response.totalPages,
      hasPreviousPage: response.hasPreviousPage,
      hasNextPage: response.hasNextPage,
    };
  }
}
